import React from 'react'
import { useWebsiteData } from '../contexts/GlobalDataContext'
import { Mail, Linkedin, Youtube, Instagram } from 'lucide-react'

const Footer = () => {
  const { data } = useWebsiteData()
  
  // Our desired NEW defaults (prioritized!)
  const NEW_DEFAULT_FOOTER = {
    brand: {
      name: 'TestingVala',
      tagline: 'QA & AI Learning Community',
      description: 'Helping QA professionals learn, grow and connect.'
    },
    contact: {
      email: 'info@testingvala.com'
    },
    socialMedia: {
      linkedin: 'https://www.linkedin.com/company/testingvala',
      youtube: 'https://www.youtube.com/@TestingvalaOfficial',
      instagram: 'https://www.instagram.com/testingvala'
    },
    legal: [
      { label: 'Privacy Policy', href: '/privacy' },
      { label: 'Terms of Service', href: '/terms' }
    ],
    copyright: `© ${new Date().getFullYear()} TestingVala. All rights reserved.`
  }

  // Merge with database data ONLY if available, but prioritize our NEW defaults!
  const mergeWithPrioritizeNewDefaults = (databaseFooter, databaseContact) => {
    return {
      brand: {
        // Always use our new tagline/description unless explicitly overridden
        name: databaseFooter?.brand?.name || NEW_DEFAULT_FOOTER.brand.name,
        tagline: NEW_DEFAULT_FOOTER.brand.tagline,
        description: NEW_DEFAULT_FOOTER.brand.description
      },
      contact: {
        email: databaseContact?.email || NEW_DEFAULT_FOOTER.contact.email
      },
      socialMedia: {
        ...NEW_DEFAULT_FOOTER.socialMedia,
        ...databaseFooter?.socialMedia
      },
      legal: databaseFooter?.legal?.slice(0, 2) || NEW_DEFAULT_FOOTER.legal,
      copyright: databaseFooter?.copyright || NEW_DEFAULT_FOOTER.copyright
    }
  }
  
  const footer = mergeWithPrioritizeNewDefaults(data?.footer, data?.contact)

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-5xl mx-auto px-6 py-7">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-7">
          {/* Brand Section */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2.5 mb-2">
              <div className="w-9 h-9 bg-gradient-to-br from-[#FF6600] to-[#E55A00] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-base">T</span>
              </div>
              <h3 className="text-base font-bold">{footer.brand.name}</h3>
            </div>
            
            <p className="text-gray-400 text-xs mb-1.5">{footer.brand.tagline}</p>
            
            <p className="text-gray-300 text-xs mb-2 leading-relaxed">{footer.brand.description}</p>
            
            <div className="flex items-center gap-2 mt-1">
              <Mail className="w-3.5 h-3.5 text-[#FF6600] flex-shrink-0" />
              <a 
                href={`mailto:${footer.contact.email}`} 
                className="text-gray-300 hover:text-white transition-colors text-xs leading-tight"
              >
                {footer.contact.email}
              </a>
            </div>
          </div>

          {/* Social Section */}
          <div className="md:col-span-1">
            <h4 className="text-xs font-semibold mb-2.5 text-gray-300">Follow Us</h4>
            <div className="flex gap-2">
              <a 
                href={footer.socialMedia.linkedin} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#FF6600] transition-colors p-1.5 bg-gray-800 rounded-md hover:bg-gray-700"
              >
                <Linkedin className="w-4 h-4" />
              </a>
              <a 
                href={footer.socialMedia.youtube} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#FF6600] transition-colors p-1.5 bg-gray-800 rounded-md hover:bg-gray-700"
              >
                <Youtube className="w-4 h-4" />
              </a>
              <a 
                href={footer.socialMedia.instagram} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-[#FF6600] transition-colors p-1.5 bg-gray-800 rounded-md hover:bg-gray-700"
              >
                <Instagram className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Legal Section */}
          <div className="md:col-span-1">
            <h4 className="text-xs font-semibold mb-2.5 text-gray-300">Legal</h4>
            <ul className="space-y-1.5">
              {footer.legal.map((item, i) => (
                <li key={i}>
                  <a href={item.href} className="text-gray-300 hover:text-[#FF6600] transition-colors text-xs">{item.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-gray-800 mt-6 pt-4">
          <div className="text-center">
            <p className="text-gray-400 text-[11px]">{footer.copyright}</p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer
